import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { IProductList } from 'types/orders';
import useFiltersState from 'state/useFiltersState';
import useUsersState from 'state/useUsersState';
import { getKAMUserFilter, getOrderFilters } from 'services/orders';
import { paymentMethodLabelsValue } from 'utils/variablesToTranslate';
import { IbackofficeUserData } from 'types/configuration.types';
import ModuleFilters, { TgetFiltersList } from 'components/ModuleFilters/ModuleFilters';
import './OrdersTrackingOperationsFilters.scss';

const OrdersTrackingOperationsFilters = ({ orderList }: { orderList?: IProductList[] }) => {
  const { ordersFilters, setOrdersFilters } = useFiltersState();
  const { users } = useUsersState();

  const [openCompanyFilter, setOpenCompanyFilter] = useState<boolean>(false);
  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openPaymentMethodFilter, setOpenPaymentMethodFilter] = useState<boolean>(false);
  const [openShipmentCountryFilter, setOpenShipmentCountryFilter] = useState<boolean>(false);
  const [openAnnotationsFilter, setOpenAnnotationsFilter] = useState<boolean>(false);
  const [openKAMFilter, setOpenKAMFilter] = useState<boolean>(false);
  const [openResponsibleFilter, setOpenResponsibleFilter] = useState<boolean>(false);
  const [openMissingFilter, setOpenMissingFilter] = useState<boolean>(false);
  const [openSlaEtaFilter, setOpenSlaEtaFilter] = useState<boolean>(false);
  const [openConfigurationFilter, setOpenConfigurationFilter] = useState<boolean>(false);
  const [openPreparationFilter, setOpenPreparationFilter] = useState<boolean>(false);
  const [openOthersFilter, setOpenOthersFilter] = useState<boolean>(false);

  const { data: kamFiltersData, error: kamFiltersError } = useSWR('kam-user-filter', getKAMUserFilter, {
    revalidateOnFocus: false
  });

  const { data: orderFiltersData } = useSWR('order-filters', getOrderFilters, {
    revalidateOnFocus: false
  });

  const kamOptionsObject = kamFiltersData || [];
  const countriesOptions = orderFiltersData?.countries || [];

  const companyFilterRawOptions = ['Empresa 1', 'Empresa 2', 'Empresa 3', 'Empresa 4', 'Empresa 5']; // TODO: debe venir del backend
  const companyFilterRawOptionsToNumberMap: Map<string, string> = companyFilterRawOptions
    ? new Map(companyFilterRawOptions?.map((company, i) => [company, i + 1]))
    : new Map();

  const ordersFilterMapping = {
    Empresa: 'company',
    Mes: 'month',
    'Método pago': 'paymentType',
    'País': 'countryDestination',
    Anotaciones: 'annotations',
    KAM: 'kam',
    Responsable: 'responsible',
    Faltantes: 'missing',
    'SLA y ETA': 'slaEta',
    Configuración: 'configuration',
    Preparación: 'preparation',
    Otros: 'others'
  };

  const monthFilterRawOptions = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ];

  const monthToNumberMap: Map<string, string> = monthFilterRawOptions
    ? new Map(monthFilterRawOptions?.map((month, i) => [month, i + 1]))
    : new Map();

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);

  const paymentMethodNameValue: Map<string, string> = new Map(
    paymentMethodLabelsValue?.map(type => [type.name, type.value])
  );

  const annotationOptionsObject = {
    'Sin seriales': 'serial',
    'Sin costos': 'purchaseValue',
    'Configuraciones adicionales procesadas': 'configDone',
    'Configuraciones adicionales pendientes': 'configPending',
    'Parada en Bodega': 'temporaryStorage',
    'Pendiente por factura': 'needInvoice'
  };
  const annotationOptionsList = new Map(Object.entries(annotationOptionsObject));
  const kamFilterRawOptions = kamOptionsObject.map(item => item?.email || '');
  const kamNameToCodeMap: Map<string, string> = new Map(kamOptionsObject?.map(type => [type.email, String(type.id)]));

  const responsibleUserIds = (orderList || []).flatMap(order => order?.responsibleIds);

  const noneResponsible = {
    email: 'Sin responsables asignados',
    id: 0,
    firstName: '',
    lastName: ''
  } as IbackofficeUserData;

  const responsibleUsers = users.filter(user => responsibleUserIds.includes(user.id));

  const responsibleUserRawOptions = [noneResponsible, ...responsibleUsers].map(user => user.email);

  const responsibleUserEmailToIdMap = new Map(
    [noneResponsible, ...responsibleUsers].map(user => [user.email, String(user.id)])
  );

  const responsibleUserEmailToNameMap = new Map(
    responsibleUsers.map(user => [user.email, `${user.firstName} ${user.lastName}`])
  );

  // Opciones para filtros estáticos TODO: integrar cuando estén disponibles en el backend
  const missingFilterOptions = ['Opción 1', 'Opción 2', 'Opción 3'];
  const slaEtaFilterOptions = ['Opción 1', 'Opción 2', 'Opción 3'];
  const configurationFilterOptions = ['Opción 1', 'Opción 2', 'Opción 3'];
  const preparationFilterOptions = ['Opción 1', 'Opción 2', 'Opción 3'];
  const othersFilterOptions = ['Opción 1', 'Opción 2', 'Opción 3'];

  const getOrdersFiltersList: TgetFiltersList = (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) => [
    {
      title: 'Empresa',
      openFunction: setOpenCompanyFilter,
      openStatus: openCompanyFilter,
      rawSelectOptions: formModuleFilters('Empresa', companyFilterRawOptions, companyFilterRawOptionsToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('company', 'value'),
      isFilterActive: getFilterActiveStatus('company')
    },
    {
      title: 'Mes',
      openFunction: setOpenMonthFilter,
      openStatus: openMonthFilter,
      rawSelectOptions: formModuleFilters('Mes', monthFilterRawOptions, monthToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('month', 'value'),
      isFilterActive: getFilterActiveStatus('month')
    },
    {
      title: 'País',
      openFunction: setOpenShipmentCountryFilter,
      openStatus: openShipmentCountryFilter,
      rawSelectOptions: formModuleFilters('País', countriesOptions),
      handleChangeSelectedOption: handleClickFilterOption('countryDestination', 'name'),
      isFilterActive: getFilterActiveStatus('countryDestination'),
      showSearch: true,
      searchPlaceholder: 'Buscar país',
      sortAlphabetically: true
    },
    {
      title: 'Método pago',
      openFunction: setOpenPaymentMethodFilter,
      openStatus: openPaymentMethodFilter,
      rawSelectOptions: formModuleFilters('Método pago', paymentMethodRawOptionName, paymentMethodNameValue),
      handleChangeSelectedOption: handleClickFilterOption('paymentType', 'value'),
      isFilterActive: getFilterActiveStatus('paymentType')
    },
    {
      title: 'Anotaciones',
      openFunction: setOpenAnnotationsFilter,
      openStatus: openAnnotationsFilter,
      rawSelectOptions: formModuleFilters('Anotaciones', Object.keys(annotationOptionsObject), annotationOptionsList),
      handleChangeSelectedOption: handleClickFilterOption('annotations', 'value'),
      isFilterActive: getFilterActiveStatus('annotations'),
      filterClass: 'annotationsOrdersClass'
    },
    {
      title: 'KAM',
      openFunction: setOpenKAMFilter,
      openStatus: openKAMFilter,
      rawSelectOptions: formModuleFilters('KAM', kamFilterRawOptions, kamNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('kam', 'value'),
      isFilterActive: getFilterActiveStatus('kam')
    },
    {
      title: 'Responsable',
      openFunction: setOpenResponsibleFilter,
      openStatus: openResponsibleFilter,
      rawSelectOptions: formModuleFilters('Responsable', responsibleUserRawOptions, responsibleUserEmailToIdMap).map(
        option => ({
          ...option,
          lineTitle: responsibleUserEmailToNameMap.get(option.name)
        })
      ),
      handleChangeSelectedOption: handleClickFilterOption('responsible', 'value'),
      isFilterActive: getFilterActiveStatus('responsible')
    },
    {
      title: 'Faltantes',
      openFunction: setOpenMissingFilter,
      openStatus: openMissingFilter,
      rawSelectOptions: formModuleFilters('Faltantes', missingFilterOptions),
      handleChangeSelectedOption: handleClickFilterOption('missing', 'name'),
      isFilterActive: getFilterActiveStatus('missing')
    },
    {
      title: 'SLA y ETA',
      openFunction: setOpenSlaEtaFilter,
      openStatus: openSlaEtaFilter,
      rawSelectOptions: formModuleFilters('SLA y ETA', slaEtaFilterOptions),
      handleChangeSelectedOption: handleClickFilterOption('slaEta', 'name'),
      isFilterActive: getFilterActiveStatus('slaEta')
    },
    {
      title: 'Configuración',
      openFunction: setOpenConfigurationFilter,
      openStatus: openConfigurationFilter,
      rawSelectOptions: formModuleFilters('Configuración', configurationFilterOptions),
      handleChangeSelectedOption: handleClickFilterOption('configuration', 'name'),
      isFilterActive: getFilterActiveStatus('configuration')
    },
    {
      title: 'Preparación',
      openFunction: setOpenPreparationFilter,
      openStatus: openPreparationFilter,
      rawSelectOptions: formModuleFilters('Preparación', preparationFilterOptions),
      handleChangeSelectedOption: handleClickFilterOption('preparation', 'name'),
      isFilterActive: getFilterActiveStatus('preparation')
    },
    {
      title: 'Otros',
      openFunction: setOpenOthersFilter,
      openStatus: openOthersFilter,
      rawSelectOptions: formModuleFilters('Otros', othersFilterOptions),
      handleChangeSelectedOption: handleClickFilterOption('others', 'name'),
      isFilterActive: getFilterActiveStatus('others')
    }
  ];

  const handleKamFiltersError = () => {
    if (kamFiltersError) {
      displayErrorNotification({
        customJSXMessage: <>Error al obtener los filtros de KAM</>
      });
    }
  };

  useEffect(() => {
    handleKamFiltersError();
  }, [kamFiltersError]);

  return (
    <div className="ordersTrackingOperationsFilters">
      <ModuleFilters
        moduleFilters={ordersFilters}
        setModuleFilters={setOrdersFilters}
        getFiltersList={getOrdersFiltersList}
        moduleFiltersTitlesMapping={ordersFilterMapping}
        customGap={14}
        optionTextCustomClass="optionTextCustomClassBussines"
        moduleFiltersIconContainerCustomClass="moduleFiltersIconContainerCustomClassOrdersTrackingOperations"
      />
    </div>
  );
};

export default OrdersTrackingOperationsFilters;
